/**
 * Calendário — uma linha por DESTINO, não por peça.
 *
 * A distinção não é detalhe de implementação: a mesma arte agendada para
 * quatro contas são quatro destinos, cada um com o próprio status, e cada um
 * pode falhar sozinho. Agrupar por peça esconderia exatamente o que se precisa
 * ver — que subiu em três e falhou numa.
 */
import { Api, type DestinoCalendario } from "../api.js";
import { dia, el, horario, limpar } from "../dom.js";
import { marcaAtual } from "../marca.js";
import { avisar, type Tela } from "../tela.js";
import { comFuso, paraCampoLocal } from "./nova-peca.js";

/** Rótulo e cor por status. `draft` e `queued` são os que confundem. */
const STATUS: Record<string, { texto: string; classe: string }> = {
  draft: { texto: "rascunho", classe: "rascunho" },
  queued: { texto: "na fila", classe: "fila" },
  publishing: { texto: "publicando", classe: "andamento" },
  published: { texto: "publicado", classe: "ok" },
  failed: { texto: "falhou", classe: "erro" },
  canceled: { texto: "cancelado", classe: "off" }
};

export function telaCalendario(): Tela {
  return {
    titulo: "Calendário",
    async montar(raiz) {
      // O calendário DA MARCA escolhida no topo. O filtro abaixo escolhe entre
      // as contas dela (Instagram, e LinkedIn quando entrar).
      const marca = marcaAtual();
      const { accounts } = await Api.contas(marca.slug);

      const filtro = el(
        "select",
        {},
        el("option", { value: "" }, `todas as contas de ${marca.nome}`)
      ) as HTMLSelectElement;
      for (const c of accounts) {
        filtro.appendChild(
          el(
            "option",
            { value: String(c.id) },
            `@${c.handle}${c.isActive ? "" : " (desligada)"}`
          )
        );
      }

      // Por padrão esconde cancelados. Destino cancelado nunca mais muda de
      // estado; deixá-lo na visão diária empurra para baixo o que ainda vai
      // acontecer, que é o que o calendário existe para mostrar.
      const porStatus = el(
        "select",
        {},
        el("option", { value: "" }, "ativos (esconde cancelados)"),
        el("option", { value: "tudo" }, "tudo, inclusive cancelados"),
        el("option", { value: "draft" }, "só rascunhos"),
        el("option", { value: "queued" }, "só na fila"),
        el("option", { value: "published" }, "só publicados"),
        el("option", { value: "failed" }, "só falhados"),
        el("option", { value: "canceled" }, "só cancelados")
      ) as HTMLSelectElement;

      const lista = el("div", { class: "agenda" });

      const carregar = async (): Promise<void> => {
        limpar(lista);
        lista.appendChild(el("p", { class: "sub" }, "carregando…"));
        try {
          const accountId = filtro.value ? Number(filtro.value) : undefined;
          const s = porStatus.value;
          const { targets } = await Api.calendario({
            brand: marca.slug,
            ...(accountId === undefined ? {} : { accountId }),
            ...(s === "" || s === "tudo" ? {} : { status: s }),
            ...(s === "tudo" ? { incluirCancelados: true } : {})
          });
          limpar(lista);
          desenhar(lista, targets, carregar, s !== "");
        } catch (err) {
          limpar(lista);
          avisar(lista, err, "Falhou ao carregar o calendário");
        }
      };

      filtro.addEventListener("change", () => void carregar());
      porStatus.addEventListener("change", () => void carregar());

      const atualizar = el("button", { class: "secundario" }, "Atualizar");
      atualizar.addEventListener("click", () => void carregar());

      raiz.appendChild(
        el("div", { class: "barra" }, filtro, porStatus, atualizar)
      );
      raiz.appendChild(lista);
      await carregar();
    }
  };
}

function desenhar(
  raiz: HTMLElement,
  destinos: DestinoCalendario[],
  recarregar: () => Promise<void>,
  filtrado = false
): void {
  if (destinos.length === 0) {
    raiz.appendChild(
      el(
        "div",
        { class: "vazio" },
        el("p", {}, filtrado ? "Nada com esse filtro." : "Nada agendado."),
        el(
          "p",
          { class: "sub" },
          filtrado
            ? "Os cancelados ficam escondidos por padrão — escolha “tudo” para vê-los."
            : "Peças novas nascem como rascunho e só entram na fila depois de aprovadas."
        )
      )
    );
    return;
  }

  // Agrupa por dia no fuso de quem está olhando. O banco guarda UTC; se o
  // agrupamento fosse pela string ISO, um post das 21h em São Paulo cairia no
  // dia seguinte.
  let diaAtual = "";
  for (const d of destinos) {
    const rotulo = dia(d.scheduledAt);
    if (rotulo !== diaAtual) {
      diaAtual = rotulo;
      raiz.appendChild(el("h3", { class: "dia" }, rotulo));
    }
    raiz.appendChild(linha(d, recarregar));
  }
}

/**
 * Painel de edição de um rascunho, aberto sobre a linha.
 *
 * Duas coisas com alcances DIFERENTES, e a tela diz qual é qual:
 *
 *   legenda   vale para a peça inteira — todos os destinos, todas as contas
 *   horário   vale só para ESTE destino
 *
 * Confundir os dois é como alguém troca a legenda do Instagram e do LinkedIn
 * achando que mexeu num só.
 */
function abrirEdicao(
  d: DestinoCalendario,
  recarregar: () => Promise<void>,
  aoFechar: () => void
): void {
  const legenda = el("textarea", { rows: "4" }) as HTMLTextAreaElement;
  legenda.value = d.body ?? "";

  const quando = el("input", { type: "datetime-local" }) as HTMLInputElement;
  quando.value = paraCampoLocal(d.scheduledAt);

  // A mídia escolhida, na ORDEM. Começa com a que a peça já tem.
  const escolhidas: number[] = d.media.map((m) => m.mediaId);
  const galeria = el("div", { class: "escolher-midia" });

  const pintarOrdem = (): void => {
    for (const botao of galeria.querySelectorAll<HTMLElement>(".opcao-midia")) {
      const id = Number(botao.dataset["id"]);
      const pos = escolhidas.indexOf(id);
      botao.classList.toggle("marcada", pos >= 0);
      const marca = botao.querySelector(".ordem");
      if (marca) marca.textContent = pos >= 0 ? String(pos + 1) : "";
    }
  };

  // A biblioteca da marca DO DESTINO, para poder trocar por qualquer outra
  // dela. Mídia de outra marca nem aparece — e a API a recusaria.
  void Api.midias(d.brand)
    .then(({ media }) => {
      limpar(galeria);
      for (const m of media) {
        const card = el(
          "button",
          { class: "opcao-midia", type: "button" },
          m.kind === "video"
            ? el("video", {
                src: m.publicUrl,
                preload: "metadata",
                muted: true
              })
            : el("img", { src: m.publicUrl, alt: "", loading: "lazy" }),
          el("span", { class: "ordem" })
        );
        card.dataset["id"] = String(m.mediaId);
        card.addEventListener("click", () => {
          const i = escolhidas.indexOf(m.mediaId);
          if (i >= 0) escolhidas.splice(i, 1);
          else escolhidas.push(m.mediaId);
          pintarOrdem();
        });
        galeria.appendChild(card);
      }
      pintarOrdem();
    })
    .catch(() => {
      limpar(galeria);
      galeria.appendChild(
        el("p", { class: "erro-linha" }, "não deu para carregar a biblioteca")
      );
    });

  const estado = el("p", { class: "sub" });
  const salvar = el(
    "button",
    { class: "pequeno" },
    "Salvar"
  ) as HTMLButtonElement;
  const fechar = el(
    "button",
    { class: "secundario pequeno" },
    "Fechar"
  ) as HTMLButtonElement;

  const painel = el(
    "div",
    { class: "editar" },
    el(
      "label",
      {},
      "Legenda ",
      el("span", { class: "sub" }, "— muda a peça inteira, todos os destinos")
    ),
    legenda,
    el(
      "label",
      {},
      "Mídia ",
      el(
        "span",
        { class: "sub" },
        "— muda a peça inteira. Clique para tirar ou pôr; o número é a ordem, e no carrossel o primeiro define a proporção"
      )
    ),
    galeria,
    el(
      "label",
      {},
      "Horário ",
      el("span", { class: "sub" }, `— muda só o destino de @${d.handle}`)
    ),
    quando,
    el("div", { class: "acoes" }, salvar, fechar),
    estado
  );

  const encerrar = (): void => {
    painel.remove();
    aoFechar();
  };
  fechar.addEventListener("click", encerrar);

  salvar.addEventListener("click", () => {
    salvar.disabled = true;
    estado.className = "sub";
    estado.textContent = "salvando…";

    void (async () => {
      try {
        const novaLegenda = legenda.value.trim() === "" ? null : legenda.value;
        const midiaMudou =
          escolhidas.length !== d.media.length ||
          escolhidas.some((id, i) => id !== d.media[i]?.mediaId);

        if (novaLegenda !== (d.body ?? null) || midiaMudou) {
          await Api.editarPeca(d.postId, {
            ...(novaLegenda !== (d.body ?? null) ? { body: novaLegenda } : {}),
            ...(midiaMudou ? { mediaIds: [...escolhidas] } : {})
          });
        }
        if (quando.value !== "" && comFuso(quando.value) !== d.scheduledAt) {
          await Api.reagendar(d.postId, d.targetId, comFuso(quando.value));
        }
        encerrar();
        await recarregar();
      } catch (err) {
        salvar.disabled = false;
        estado.className = "erro-linha";
        estado.textContent = err instanceof Error ? err.message : String(err);
      }
    })();
  });

  // Abre logo abaixo da linha, e não numa modal: a modal esconderia o
  // calendário, que é justamente o contexto de quem está editando.
  const linhaDom = document.querySelector<HTMLElement>(
    `[data-destino="${d.targetId}"]`
  );
  linhaDom?.insertAdjacentElement("afterend", painel);
}

function linha(
  d: DestinoCalendario,
  recarregar: () => Promise<void>
): HTMLElement {
  const st = STATUS[d.status] ?? { texto: d.status, classe: "" };

  const acoes = el("div", { class: "acoes-linha" });

  // ------------------------------------------------- ações de rascunho
  //
  // Existiam só na tela de Nova peça, logo depois de criar. Quem saísse dali
  // deixava o rascunho encalhado, sem forma de aprovar pela interface — que é
  // exatamente o que o Caio encontrou.
  if (d.status === "draft") {
    const aprovar = el(
      "button",
      { class: "pequeno" },
      "Aprovar"
    ) as HTMLButtonElement;

    aprovar.addEventListener("click", () => {
      // O horário já passou? Então não é "publica no horário agendado" — é
      // publica AGORA, no próximo ciclo.
      //
      // O worker busca `scheduled_at <= agora` sem limite inferior, o que é o
      // comportamento certo: ele existe para publicar o que está vencido. Mas
      // aprovar um rascunho esquecido de semana passada o põe no ar em
      // minutos, e a confirmação precisa dizer isso — prometer "no horário
      // agendado" seria mentira justamente no caso que surpreende.
      const atraso = Date.now() - new Date(d.scheduledAt).getTime();
      const jaVenceu = atraso > 0;
      const dias = Math.floor(atraso / 86_400_000);

      const quando = jaVenceu
        ? `O horário agendado (${horario(d.scheduledAt)}) JÁ PASSOU` +
          (dias >= 1 ? ` há ${dias} dia(s)` : "") +
          `, então ela vai ao ar no próximo ciclo — em até 5 minutos.`
        : `Ela vai ao ar em ${horario(d.scheduledAt)}.`;

      if (
        !confirm(
          `Aprovar a peça ${d.postId}?\n\n` +
            `${quando}\n\n` +
            `Vale para TODOS os destinos dela, não só este. Publica no perfil ` +
            `real, e post publicado não se edita nem se remove por API.`
        )
      )
        return;
      aprovar.disabled = true;
      void Api.enfileirar(d.postId)
        .then(() => recarregar())
        .catch((err: unknown) => {
          aprovar.disabled = false;
          alert(err instanceof Error ? err.message : String(err));
        });
    });
    acoes.appendChild(aprovar);

    const editar = el(
      "button",
      { class: "secundario pequeno" },
      "Editar"
    ) as HTMLButtonElement;
    editar.addEventListener("click", () => {
      editar.disabled = true;
      abrirEdicao(d, recarregar, () => {
        editar.disabled = false;
      });
    });
    acoes.appendChild(editar);
  }

  // Desaprovar devolve para rascunho, para poder editar e aprovar de novo.
  //
  // Antes, aprovar era caminho de mão única na interface: depois de aprovado
  // só restava cancelar — e cancelar PERDE o destino em vez de devolvê-lo.
  if (d.status === "queued") {
    const desap = el(
      "button",
      { class: "secundario pequeno" },
      "Desaprovar"
    ) as HTMLButtonElement;

    desap.addEventListener("click", () => {
      if (
        !confirm(
          `Tirar a peça ${d.postId} da fila?\n\n` +
            `Os destinos voltam a rascunho e a peça fica editável de novo. ` +
            `Vale para TODOS os destinos em fila, não só este.\n\n` +
            `Se o worker já tiver começado a publicar algum, esse segue — ` +
            `parar no meio é o que produz o desfecho ambíguo que exige ` +
            `conferência humana.`
        )
      )
        return;

      desap.disabled = true;
      void Api.desaprovar(d.postId)
        .then(async (r) => {
          if (r.devolvidos === 0) alert(r.nota);
          await recarregar();
        })
        .catch((err: unknown) => {
          desap.disabled = false;
          alert(err instanceof Error ? err.message : String(err));
        });
    });
    acoes.appendChild(desap);
  }

  // Cancelar só em draft ou queued. Depois de `published` o post está no
  // perfil, e o endpoint recusa — o banco não vai mentir sobre isso.
  if (d.status === "draft" || d.status === "queued") {
    const b = el(
      "button",
      { class: "perigo pequeno" },
      "Cancelar"
    ) as HTMLButtonElement;
    b.addEventListener("click", () => {
      if (
        !confirm(
          `Cancelar o destino de @${d.handle} em ${horario(d.scheduledAt)}?\n\n` +
            `Os outros destinos desta mesma peça não são afetados.`
        )
      )
        return;
      b.disabled = true;
      void Api.cancelarDestino(d.postId, d.targetId)
        .then(() => recarregar())
        .catch((err: unknown) => {
          b.disabled = false;
          alert(err instanceof Error ? err.message : String(err));
        });
    });
    acoes.appendChild(b);
  }

  // Apagar a peça inteira, para limpar rascunho e cancelamento que se
  // acumulam num teste. Só aparece quando o destino não foi ao ar — a API
  // recusa o resto, e oferecer um botão que sempre falha seria pior.
  if (d.status === "draft" || d.status === "canceled") {
    const ap = el(
      "button",
      { class: "perigo pequeno" },
      "Apagar peça"
    ) as HTMLButtonElement;

    ap.addEventListener("click", () => {
      if (
        !confirm(
          `Apagar a peça ${d.postId} inteira?\n\n` +
            `Some do calendário junto com TODOS os destinos dela, inclusive de ` +
            `outras contas. A mídia NÃO é apagada — ela volta a ficar removível ` +
            `na aba Mídia.`
        )
      )
        return;

      ap.disabled = true;
      void Api.apagarPeca(d.postId)
        .then(() => recarregar())
        .catch((err: unknown) => {
          ap.disabled = false;
          alert(err instanceof Error ? err.message : String(err));
        });
    });
    acoes.appendChild(ap);
  }

  if (d.permalink) {
    acoes.appendChild(
      el(
        "a",
        {
          href: d.permalink,
          target: "_blank",
          rel: "noopener noreferrer",
          class: "pequeno"
        },
        "ver no perfil"
      )
    );
  }

  // A miniatura, porque "1 mídia(s)" não diz qual arte é.
  //
  // Mostra até três e um contador para o resto: num carrossel de dez, dez
  // miniaturas fariam a linha do calendário virar uma galeria e esconder o
  // horário, que é o que se lê primeiro.
  const capa =
    d.media.length === 0
      ? null
      : el(
          "div",
          { class: "capa" },
          ...d.media.slice(0, 3).map((m) =>
            m.kind === "video"
              ? el("video", {
                  src: m.publicUrl,
                  preload: "metadata",
                  muted: true
                })
              : el("img", { src: m.publicUrl, alt: "", loading: "lazy" })
          ),
          d.media.length > 3
            ? el("span", { class: "mais" }, `+${d.media.length - 3}`)
            : null
        );

  return el(
    "article",
    { class: `destino ${st.classe}`, "data-destino": String(d.targetId) },
    el(
      "div",
      { class: "quando" },
      el("strong", {}, horario(d.scheduledAt).split(" ")[1] ?? ""),
      el("span", { class: "pilula " + st.classe }, st.texto)
    ),
    capa,
    el(
      "div",
      { class: "oque" },
      el(
        "div",
        { class: "titulo-linha" },
        el("strong", {}, `@${d.handle}`),
        el("span", { class: "formato" }, d.format),
        d.mediaCount > 0
          ? el("span", { class: "sub" }, `${d.mediaCount} mídia(s)`)
          : null
      ),
      d.body
        ? el("p", { class: "legenda" }, d.body)
        : el("p", { class: "sub" }, "sem legenda"),
      // O último erro é o que explica a falha, e vem escrito para gente.
      // Escondê-lo obrigaria a ir ao banco justamente na hora ruim.
      d.lastError ? el("p", { class: "erro-linha" }, d.lastError) : null,
      d.attempts > 0
        ? el("p", { class: "sub" }, `${d.attempts} tentativa(s)`)
        : null
    ),
    acoes
  );
}
